import {
  Children,
  cloneElement,
  isValidElement,
  type ReactElement,
  type ReactNode,
} from "react";

function rewriteCssUrlIds(value: string, idPrefix: string): string {
  return value.replace(
    /url\(#([^)]+)\)/g,
    (_match, id) => `url(#${idPrefix}-${id})`,
  );
}

function rewritePropsWithPrefix(
  props: Record<string, unknown>,
  idPrefix: string,
): Record<string, unknown> {
  const rewritten: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(props)) {
    if (typeof value === "string") {
      if (key === "id") {
        rewritten[key] = `${idPrefix}-${value}`;
        continue;
      }

      if ((key === "href" || key === "xlinkHref") && value.startsWith("#")) {
        rewritten[key] = `#${idPrefix}-${value.slice(1)}`;
        continue;
      }

      rewritten[key] = rewriteCssUrlIds(value, idPrefix);
      continue;
    }

    if (
      key === "style" &&
      value &&
      typeof value === "object" &&
      !Array.isArray(value)
    ) {
      const styleEntries = Object.entries(value as Record<string, unknown>).map(
        ([styleKey, styleValue]) => {
          if (typeof styleValue === "string") {
            return [styleKey, rewriteCssUrlIds(styleValue, idPrefix)];
          }

          return [styleKey, styleValue];
        },
      );

      rewritten[key] = Object.fromEntries(styleEntries);
      continue;
    }

    rewritten[key] = value;
  }

  return rewritten;
}

function rewriteTreeIds(node: ReactNode, idPrefix: string): ReactNode {
  if (!isValidElement(node)) {
    return node;
  }

  const props = node.props as Record<string, unknown>;
  const rewrittenChildren = Children.map(props.children as ReactNode, (child) =>
    rewriteTreeIds(child, idPrefix),
  );
  const rewrittenProps = rewritePropsWithPrefix(props, idPrefix);

  return cloneElement(node, rewrittenProps, rewrittenChildren);
}

/**
 * Rewrites SVG definition references in a rendered icon tree to be unique per instance.
 *
 * Why: many icons rely on internal `id` values (for gradients, clip paths, filters, etc.).
 * Rendering multiple inline copies of the same SVG can cause collisions, where one instance
 * accidentally resolves definitions from another. This can lead to wrong colors/fills or
 * broken rendering in some browsers (notably Safari).
 */
export function withUniqueSvgIds(
  svg: ReactElement,
  idPrefix: string,
): ReactElement {
  return rewriteTreeIds(svg, idPrefix) as ReactElement;
}
